import { ListItem, UnorderedList } from '@chakra-ui/react';
import { LocalStorageId } from '@resistapp/client/components/shared/local-storage';
import { useChartDownload } from '@resistapp/client/contexts/chart-download-context';
import { useSearchParamsContext } from '@resistapp/client/contexts/search-params-context';
import { AbunanceSelection } from '@resistapp/client/data-utils/filter-data/filter';
import { useNormalisationMode } from '@resistapp/client/hooks/use-query-filters/use-query-filters';
import { useResearchAdmin } from '@resistapp/client/hooks/use-research-admin';
import { isConsideredAbsolute, NormalisedValueName } from '@resistapp/common/normalisation-mode';
import { scaleOrdinal } from '@visx/scale';
import { useEffect } from 'react';
import { BaseLegend } from './base-legend';
import { ALL_COLORS, getBinLabels, QUANTIFIED_ONLY_COLORS } from './heatmap-bins';
import { Legend } from './legend';

interface HeatmapLegendProps {
  setAbundanceMode: (mode: AbunanceSelection) => void;
  abundanceMode: AbunanceSelection;
  legendHeight: string;
  id?: string;
}

export function HeatmapLegend({ abundanceMode, setAbundanceMode, legendHeight, id }: HeatmapLegendProps) {
  const normalisationMode = useNormalisationMode();
  const { isModifyAsAdminActive } = useResearchAdmin();
  const { setSearchParamsStable } = useSearchParamsContext();
  const bins = getBinLabels(normalisationMode, abundanceMode);
  const colors = abundanceMode === AbunanceSelection.QUANTIFIED_ONLY ? QUANTIFIED_ONLY_COLORS : ALL_COLORS;
  const colorScale = scaleOrdinal<string, string>({ domain: bins, range: colors });
  const labelFormat = (label: string | number) => String(label);
  const option = heatmapOptions.find(o => o.value === abundanceMode) as Option;
  const numberExplanation = isConsideredAbsolute(normalisationMode)
    ? 'The colors in the heatmap show an estimation of gene copy numbers.'
    : 'The colors in the heatmap show gene abundances relative to 16S rRNA gene.';
  const description = NormalisedValueName[normalisationMode];
  const { downloadChartAndLegend } = useChartDownload();

  // Hook to automatically reset mode when admin editing is turned off
  useResetAbundanceModeOnAdminToggle(isModifyAsAdminActive, abundanceMode, setAbundanceMode, setSearchParamsStable);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '10px',
      }}
    >
      <BaseLegend
        id={id}
        header="Genes in detail"
        description={description}
        options={heatmapOptions.filter(
          o =>
            isModifyAsAdminActive ||
            (o.value !== AbunanceSelection.ESTIMATES && o.value !== AbunanceSelection.MEASURED_OR_ESTIMATE),
        )}
        onChange={value => {
          const mode = value as AbunanceSelection;
          setAbundanceMode(mode);
          setSearchParamsStable(prev => {
            const next = new URLSearchParams(prev.toString());
            if (mode === AbunanceSelection.MEASURED_OR_ESTIMATE) {
              next.set('estimations', 'true');
            } else {
              next.delete('estimations');
            }
            return next;
          });
        }}
        defaultValue={option.value}
        TooltipContent={
          <UnorderedList>
            <ListItem>Heatmap colors correspond to ranges between logaritmic axis ticks on the box plot.</ListItem>
            <ListItem>{numberExplanation}</ListItem>
            <ListItem>Use this drop-down to focus on detected genes only.</ListItem>
            <ListItem>You can limit the genes further by the above gene group selection.</ListItem>
          </UnorderedList>
        }
        onDownload={
          id
            ? () => {
                downloadChartAndLegend({
                  legendId: id,
                  chartId: 'research-view-gene-details-chart',
                  plotType: 'heat',
                  label: 'Gene_details',
                });
              }
            : undefined
        }
      >
        <Legend colorScale={colorScale} labelFormat={labelFormat} getOpacity={() => 1} height={legendHeight} />
      </BaseLegend>
    </div>
  );
}

// Reset restricted abundance modes when admin mode is disabled
export function useResetAbundanceModeOnAdminToggle(
  isAdminMode: boolean,
  abundanceMode: AbunanceSelection,
  setAbundanceMode: (mode: AbunanceSelection) => void,
  setSearchParamsStable: ReturnType<typeof useSearchParamsContext>['setSearchParamsStable'],
) {
  useEffect(() => {
    if (
      !isAdminMode &&
      (abundanceMode === AbunanceSelection.ESTIMATES || abundanceMode === AbunanceSelection.MEASURED_OR_ESTIMATE)
    ) {
      setAbundanceMode(AbunanceSelection.ANALYSED);
      localStorage.removeItem(LocalStorageId.abundanceMode);
      setSearchParamsStable(prev => {
        const next = new URLSearchParams(prev.toString());
        next.delete('estimations');
        return next;
      });
    }

    // Ensure the "estimations" param is only present when the measured-or-estimate mode is active
    if (abundanceMode !== AbunanceSelection.MEASURED_OR_ESTIMATE) {
      setSearchParamsStable(prev => {
        if (!prev.get('estimations')) return prev;
        const next = new URLSearchParams(prev.toString());
        next.delete('estimations');
        return next;
      });
    }
  }, [isAdminMode, abundanceMode, setAbundanceMode, setSearchParamsStable]);
}

export const heatmapOptions: Option[] = [
  {
    label: 'All analysed genes',
    value: AbunanceSelection.ANALYSED,
  },
  {
    label: 'Detected and traces',
    value: AbunanceSelection.QUANTIFIED_AND_TRACES,
  },
  {
    label: 'Detected only',
    value: AbunanceSelection.QUANTIFIED_ONLY,
  },
  {
    label: 'ARG2.1 + OneHealth',
    value: AbunanceSelection.MEASURED_OR_ESTIMATE,
  },
  {
    label: 'Estimates',
    value: AbunanceSelection.ESTIMATES,
  },
];

interface Option {
  label: string;
  value: AbunanceSelection;
}
